/**
 * Routing map (Task 16, 2026-10-04) — action × step → model chips, plus the
 * vibes that override a step's model. Sits below the Actions cards so an
 * operator can see every step's whole chain (primary, then dashed
 * fallbacks) and every vibe override at a glance, not just the one action
 * selected in the sidebar.
 *
 * Clicking a chip highlights every row that uses that exact model — the same
 * click-to-highlight idea as CapabilityGrid's "Used by" column, applied here
 * across the whole action/vibe surface instead of per-model.
 */
import React, { useState } from 'react';
import type { AiModelDto } from '../../../services/aiControl/registry';
import type { AiActionDto, AiVibeDto, ModelRefDto } from '../../../services/aiControl/rubyAi';
import { ACTION_GROUP_ORDER, GROUP_LABELS, modelRef } from './catalogModel';
import { Card, Mono } from './ui';

export interface RoutingChip {
  ref: string;
  label: string;
  dashed: boolean;
}

export interface RoutingRow {
  key: string;
  actionId: string;
  actionLabel: string;
  /** "main" for a single-step action, else the step id ("draw", "background"). */
  stepLabel: string;
  kind: 'step' | 'vibe';
  vibeLabel?: string;
  chips: RoutingChip[];
}

const label = (models: AiModelDto[], ref: ModelRefDto | undefined): string => {
  if (!ref?.providerId || !ref?.modelId) return '—';
  const m = models.find((x) => x.providerId === ref.providerId && x.modelId === ref.modelId);
  return m?.label ?? `${ref.providerId}/${ref.modelId}`;
};

const chipsFor = (models: AiModelDto[], primary: ModelRefDto | undefined, fallbacks: ModelRefDto[]): RoutingChip[] => {
  const chips: RoutingChip[] = [];
  if (primary?.providerId && primary?.modelId) chips.push({ ref: modelRef(primary.providerId, primary.modelId), label: label(models, primary), dashed: false });
  for (const f of fallbacks) {
    if (!f?.providerId || !f?.modelId) continue;
    chips.push({ ref: modelRef(f.providerId, f.modelId), label: label(models, f), dashed: true });
  }
  return chips;
};

/** PURE — the rows the map renders, built from the live actions/vibes/models. */
export function buildRoutingRows(actions: AiActionDto[], vibes: AiVibeDto[], models: AiModelDto[]): RoutingRow[] {
  const rows: RoutingRow[] = [];
  const sorted = [...actions].sort(
    (a, b) => ACTION_GROUP_ORDER.indexOf(a.group) - ACTION_GROUP_ORDER.indexOf(b.group) || a.label.localeCompare(b.label),
  );
  for (const a of sorted) {
    if (a.steps?.length) {
      for (const s of a.steps) {
        rows.push({
          key: `${a.actionId}:${s.id}`, actionId: a.actionId, actionLabel: a.label, stepLabel: s.id, kind: 'step',
          chips: chipsFor(models, { providerId: s.providerId, modelId: s.modelId }, s.fallbacks ?? []),
        });
      }
    } else {
      rows.push({
        key: `${a.actionId}:main`, actionId: a.actionId, actionLabel: a.label, stepLabel: 'main', kind: 'step',
        chips: chipsFor(
          models,
          { providerId: a.providerId, modelId: a.modelId },
          a.fallbackProviderId && a.fallbackModelId ? [{ providerId: a.fallbackProviderId, modelId: a.fallbackModelId }] : [],
        ),
      });
    }
    for (const v of vibes) {
      const override = v.modelOverrides?.[a.actionId] ?? (v.actionIds.includes(a.actionId) && v.providerId && v.modelId
        ? { providerId: v.providerId, modelId: v.modelId }
        : undefined);
      if (!override) continue;
      rows.push({
        key: `${a.actionId}:vibe:${v.vibeId}`, actionId: a.actionId, actionLabel: a.label, stepLabel: `vibe · ${v.label}`,
        kind: 'vibe', vibeLabel: v.label, chips: chipsFor(models, override, []),
      });
    }
  }
  return rows;
}

const RoutingMap: React.FC<{ actions: AiActionDto[]; vibes: AiVibeDto[]; models: AiModelDto[] }> = ({ actions, vibes, models }) => {
  const [selected, setSelected] = useState<string | null>(null);
  const rows = buildRoutingRows(actions, vibes, models);

  if (!rows.length) return null;

  return (
    <Card className="overflow-hidden">
      <div className="px-4 md:px-5 py-3 border-b border-stone-100">
        <h2 className="text-sm font-semibold text-stone-900">Routing map</h2>
        <p className="text-[12px] text-stone-600 mt-0.5">
          Every action step's model chain — solid chip is the default, dashed chips are tried in order if it fails.
          Vibe overrides are listed under the action they replace. Click a model to see everywhere it is used.
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead>
            <tr className="bg-stone-50 text-[11px] uppercase tracking-wide text-stone-600">
              <th className="text-left font-semibold px-4 py-2.5">Action</th>
              <th className="text-left font-semibold px-3 py-2.5">Step</th>
              <th className="text-left font-semibold px-3 py-2.5">Model chain</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const highlighted = !!selected && r.chips.some((c) => c.ref === selected);
              return (
                <tr key={r.key} className={`border-t border-stone-100 ${highlighted ? 'bg-[#FFF1F3]' : ''}`}>
                  <td className="px-4 py-2 text-stone-800 font-medium">{r.kind === 'vibe' ? '' : GROUP_LABELS[actions.find((a) => a.actionId === r.actionId)?.group ?? 'text']} {r.actionLabel}</td>
                  <td className="px-3 py-2">
                    <Mono className={r.kind === 'vibe' ? 'text-[#9F1239]' : 'text-stone-600'}>{r.stepLabel}</Mono>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {r.chips.length === 0 ? (
                        <span className="text-stone-400">—</span>
                      ) : r.chips.map((c, i) => (
                        <React.Fragment key={`${c.ref}-${i}`}>
                          {i > 0 && <span className="text-stone-400">→</span>}
                          <button
                            type="button"
                            onClick={() => setSelected((s) => (s === c.ref ? null : c.ref))}
                            aria-pressed={selected === c.ref}
                            className={`min-h-[44px] px-2.5 rounded-full text-[12px] font-medium border inline-flex items-center ${c.dashed ? 'border-dashed' : ''} ${selected === c.ref ? 'border-[#DC143C] bg-[#FFF1F3] text-[#9F1239]' : 'border-stone-300 bg-white text-stone-700 hover:border-stone-400'}`}
                          >
                            {c.label}
                          </button>
                        </React.Fragment>
                      ))}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

export default RoutingMap;
